import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_GROUPS, MODULES } from "../../data/landings";
import LanguageSwitcher from "./LanguageSwitcher";

export const Header = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [modulesOpen, setModulesOpen] = useState(false);
  const modulesRef = useRef(null);
  const location = useLocation();
  // BrowserRouter basename takes care of locale prefix — paths here are always
  // relative to the current locale (e.g. "/" is the current locale's home).
  const isHome = location.pathname === "/";

  // Mapping NAV_GROUPS labels → i18n keys (graceful fallback to original).
  const navLabel = (g) => {
    const map = {
      "/eigen-paard": t("nav.voorEigenaren", g.label),
      "/manege-ruiter": t("nav.voorManegeRuiters", g.label),
      "/trainer": t("nav.voorTrainers", g.label),
      "/professional": t("nav.voorProfessionals", g.label),
    };
    return map[g.href] || g.label;
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (modulesRef.current && !modulesRef.current.contains(e.target)) {
        setModulesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    setOpen(false);
    setModulesOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-[hsl(var(--border))] bg-background/80 backdrop-blur-xl"
          : "bg-background/0"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-[hsl(var(--forest))] ring-1 ring-[hsl(var(--forest-dark))]/30">
            <img
              src="/assets/logo-square.png"
              alt="EquiSight logo"
              className="h-full w-full object-cover"
            />
          </span>
          <span className="font-display text-2xl font-semibold tracking-tight text-[hsl(var(--ink))]">
            EquiSight
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          <Link
            to={"/"}
            data-testid="nav-home"
            className="text-sm text-[hsl(var(--ink-soft))] transition-colors hover:text-[hsl(var(--ink))]"
          >
            {t("nav.home")}
          </Link>
          {NAV_GROUPS.map((g) => (
            <Link
              key={g.href}
              to={g.href}
              data-testid={`nav-${g.href.replace("/", "")}`}
              className="text-sm text-[hsl(var(--ink-soft))] transition-colors hover:text-[hsl(var(--ink))]"
            >
              {navLabel(g)}
            </Link>
          ))}

          <div ref={modulesRef} className="relative">
            <button
              type="button"
              onClick={() => setModulesOpen((v) => !v)}
              data-testid="nav-modules-trigger"
              className="flex items-center gap-1 text-sm text-[hsl(var(--ink-soft))] transition-colors hover:text-[hsl(var(--ink))]"
            >
              {t("nav.modules")}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${modulesOpen ? "rotate-180" : ""}`}
                strokeWidth={1.8}
              />
            </button>
            {modulesOpen && (
              <div
                data-testid="nav-modules-menu"
                className="absolute right-0 top-full mt-3 w-64 overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] py-2 shadow-[0_30px_60px_-25px_rgba(26,37,30,0.25)]"
              >
                {MODULES.map((m) => (
                  <Link
                    key={m.href}
                    to={m.href}
                    onClick={() => setModulesOpen(false)}
                    className="block px-5 py-2.5 text-sm text-[hsl(var(--ink))] transition-colors hover:bg-[hsl(var(--surface-secondary))]"
                  >
                    {m.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {isHome ? (
            <a
              href="#pricing"
              data-testid="nav-pricing"
              className="text-sm text-[hsl(var(--ink-soft))] transition-colors hover:text-[hsl(var(--ink))]"
            >
              {t("nav.prijzen")}
            </a>
          ) : (
            <Link
              to={`${"/"}#pricing`}
              data-testid="nav-pricing"
              className="text-sm text-[hsl(var(--ink-soft))] transition-colors hover:text-[hsl(var(--ink))]"
            >
              {t("nav.prijzen")}
            </Link>
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <a
            href={isHome ? "#waitlist" : `${"/"}#waitlist`}
            data-testid="header-cta-download"
            className="rounded-full bg-[hsl(var(--forest))] px-5 py-2.5 text-sm font-medium text-[hsl(var(--background))] transition-all hover:bg-[hsl(var(--forest-dark))]"
          >
            {t("common.betaTester")}
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher variant="compact" />
          <button
            type="button"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-full border border-[hsl(var(--border))]"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-[hsl(var(--border))] bg-background/95 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col gap-1 px-6 py-6">
            <Link to={"/"} className="py-2 text-base text-[hsl(var(--ink))]">{t("nav.home")}</Link>
            {NAV_GROUPS.map((g) => (
              <Link key={g.href} to={g.href} className="py-2 text-base text-[hsl(var(--ink))]">
                {navLabel(g)}
              </Link>
            ))}
            <p className="mt-4 text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">
              {t("nav.modules")}
            </p>
            {MODULES.map((m) => (
              <Link key={m.href} to={m.href} className="py-2 text-sm text-[hsl(var(--ink-soft))]">
                {m.label}
              </Link>
            ))}
            <a href={isHome ? "#pricing" : `${"/"}#pricing`} className="mt-4 py-2 text-base text-[hsl(var(--ink))]">
              {t("nav.prijzen")}
            </a>
            <a
              href={isHome ? "#waitlist" : `${"/"}#waitlist`}
              className="mt-4 rounded-full bg-[hsl(var(--forest))] px-5 py-3 text-center text-sm font-medium text-[hsl(var(--background))]"
            >
              {t("common.betaTester")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
